import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Divider,
  IconButton,
  Collapse,
  useTheme,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  ExpandMore,
  ExpandLess,
  ArrowDropUp,
  ArrowDropDown,
  AccessTime,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const DateTimeInput = React.forwardRef(({ value, onClick, label }, ref) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value}
    onClick={onClick}
    ref={ref}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <AccessTime fontSize="small" color="action" />
        </InputAdornment>
      ),
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        "& fieldset": {
          borderColor: "divider",
        },
        "&:hover fieldset": {
          borderColor: "primary.main",
        },
        "&.Mui-focused fieldset": {
          borderColor: "primary.main",
          borderWidth: "1px",
        },
      },
    }}
  />
));

const formatCurrency = (value, currency) => {
  if (value === undefined || value === null) return "N/A";

  const numValue = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(numValue)) return "N/A";

  switch (currency) {
    case "USD":
      return `$${numValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case "EUR":
      return `€${numValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    default:
      return `؋${numValue.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
  }
};

const ProfessionalTable = ({ title, rows, columns, onRowClick }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        borderLeft: "4px solid",
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
        width: "100%",
        transition: "box-shadow 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            flexGrow: 1,
          }}
        >
          {title}
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.main,
                "& th": {
                  borderBottom: "none",
                },
              }}
            >
              {columns.map((col, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.common.white,
                    fontSize: "0.875rem",
                  }}
                >
                  {col.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={index}
                hover
                onClick={() => onRowClick?.(row, title)}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  "&:nth-of-type(odd)": {
                    bgcolor: theme.palette.action.hover,
                  },
                  "&:hover": {
                    bgcolor: theme.palette.action.selected,
                  },
                  ...(row.isTotal && {
                    bgcolor: `${theme.palette.primary.light} !important`,
                    "& td": { fontWeight: 600 },
                  }),
                }}
              >
                {columns.map((col, colIndex) => (
                  <TableCell
                    key={colIndex}
                    sx={{
                      fontSize: "0.875rem",
                      color:
                        typeof row[col] === "number" && row[col] < 0
                          ? theme.palette.error.main
                          : "inherit",
                    }}
                  >
                    {col.toLowerCase().includes("amount") ||
                    col.toLowerCase().includes("value") ||
                    col.toLowerCase().includes("revenue") ||
                    col.toLowerCase().includes("expense") ||
                    col.toLowerCase().includes("sales")
                      ? formatCurrency(row[col], row.currency || "AFN")
                      : row[col] ?? "N/A"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const SummaryCard = ({ title, value, color, change, onClick }) => {
  const theme = useTheme();
  const isPositive = typeof change === "number" ? change >= 0 : true;
  const absChange = Math.abs(change || 0);

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: color?.background || theme.palette.background.paper,
        textAlign: "center",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderLeft: "4px solid",
        borderColor: color?.border || theme.palette.primary.main,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: onClick ? "translateY(-2px)" : "none",
          boxShadow: onClick ? theme.shadows[4] : "none",
        },
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: theme.palette.text.secondary,
          mb: 1,
          fontWeight: 500,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: color?.text || theme.palette.text.primary,
          mb: 1,
        }}
      >
        {typeof value === "number" ? formatCurrency(value, "AFN") : value}
      </Typography>
      {typeof change === "number" && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          color={
            isPositive ? theme.palette.success.main : theme.palette.error.main
          }
        >
          {isPositive ? <ArrowDropUp /> : <ArrowDropDown />}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {absChange}%
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const Report = () => {
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [timeRange, setTimeRange] = useState("all");
  const [fetchParams, setFetchParams] = useState(
    "finance-report/overall_finance_report/"
  );
  const [expandedSections, setExpandedSections] = useState([]);
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["finance-report", fetchParams],
    fetchParams,
    token
  );

  const reportRef = useRef();

  const formatDateForBackend = (date) => {
    if (!date) return null;

    // Get the local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    // Format as YYYY-MM-DD HH:MM:SS
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleFilter = () => {
    const params = new URLSearchParams();
    if (startDate) {
      params.append("start_date", formatDateForBackend(startDate));
    }
    if (endDate) {
      params.append("end_date", formatDateForBackend(endDate));
    }
    setFetchParams(
      `finance-report/overall_finance_report/?${params.toString()}`
    );
    refetch();
  };

  const handleTimeRangeChange = (e) => {
    const range = e.target.value;
    setTimeRange(range);

    const now = new Date();
    switch (range) {
      case "hour":
        setStartDate(new Date(now.getTime() - 60 * 60 * 1000));
        setEndDate(new Date(now));
        break;
      case "day":
        setStartDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
        setEndDate(new Date(now));
        break;
      case "week":
        setStartDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
        setEndDate(new Date(now));
        break;
      case "month":
        setStartDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
        setEndDate(new Date(now));
        break;
      case "all":
      default:
        // Don't reset dates when selecting "All Time"
        break;
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleRowClick = (row, sectionTitle) => {
    const routeMap = {
      "Procedure Revenue": "/reception/patient-list",
      "HT Patient Revenue": "/finance/fees-collection/ht-patient",
      Appointments: "/reception/appointments",
      Laboratory: "/finance/fees-collection/laboratory",
      Radiology: "/finance/fees-collection/xray",
      "Employee Financials": "/finance/advance",
      "Payroll & Expenses": "/finance/payroll",
      "Medicine Sales": "/pharmacy/pos",
      "Prescription Sales": "/finance/payment-collection",
      Echo: "/finance/fees-collection/echo",
      Purchases: "/pharmacy/purchase-drug",
      Dental: "/finance/fees-collection/dental",
    };

    const route = routeMap[sectionTitle];
    if (route) navigate(route);
  };

  const handleSummaryClick = (sectionTitle) => {
    const routeMap = {
      "Total Income": "/reception/patient-list",
      "Total Expenses": "/finance/expenses",
      "Net Income": "/finance",
    };

    const route = routeMap[sectionTitle];
    if (route) navigate(route);
  };

  const calculateCollectedAmount = () => {
    let collected = 0;

    // Add collected amounts from HT Patient (AFN only)
    if (data?.ht_patient?.Afn?.collected_amount) {
      collected += data.ht_patient.Afn.collected_amount;
    }

    // Add collected amounts from Procedure (AFN only)
    if (data?.procedure?.Afn?.collected_amount) {
      collected += data.procedure.Afn.collected_amount;
    }

    // Add collected amounts from other sources
    if (data?.appointment?.total_revenue_from_appointments) {
      collected += data.appointment.total_revenue_from_appointments;
    }

    if (data?.lab?.total_lab_revenue) {
      collected += data.lab.total_lab_revenue;
    }

    if (data?.radiology?.xray_revenue) {
      collected += data.radiology.xray_revenue;
    }

    if (data?.radiology?.ultrasound_revenue) {
      collected += data.radiology.ultrasound_revenue;
    }

    if (data?.medicine_sales?.total_medicine_sales) {
      collected += data.medicine_sales.total_medicine_sales;
    }

    if (data?.prescription?.total_prescription_sales) {
      collected += data.prescription.total_prescription_sales;
    }

    if (data?.echo?.total_echo_revenue) {
      collected += data.echo.total_echo_revenue;
    }

    if (data?.dental?.collected_amount) {
      collected += data.dental.collected_amount;
    }

    return collected;
  };

  const procedureRows = Object.entries(data?.procedure || {}).map(
    ([currency, values]) => ({
      currency,
      total_amount: values?.total_amount ?? 0,
      collected_amount: values?.collected_amount ?? 0,
      remaining_amount: values?.remaining_amount ?? 0,
    })
  );

  const htPatientRows = Object.entries(data?.ht_patient || {}).map(
    ([currency, values]) => ({
      currency,
      total_amount: values?.total_amount ?? 0,
      collected_amount: values?.collected_amount ?? 0,
      remaining_amount: values?.remaining_amount ?? 0,
    })
  );

  const employeeFinancialsData = data?.employee_financials || {};

  const allSections = [
    {
      id: "procedure",
      title: "Procedure Revenue",
      tableData: {
        rows: procedureRows,
        columns: [
          "currency",
          "total_amount",
          "collected_amount",
          "remaining_amount",
        ],
      },
      isCurrencyBased: true,
    },
    {
      id: "ht_patient",
      title: "HT Patient Revenue",
      tableData: {
        rows: htPatientRows,
        columns: [
          "currency",
          "total_amount",
          "collected_amount",
          "remaining_amount",
        ],
      },
      isCurrencyBased: true,
    },
    {
      id: "appointments",
      title: "Appointments",
      data: {
        "Total Revenue": data?.appointment?.total_revenue_from_appointments,
        "Unpaid Amount": data?.appointment?.total_pending_fees,
      },
    },
    {
      id: "lab",
      title: "Laboratory",
      data: {
        "Total Revenue": data?.lab?.total_lab_revenue,
        "Unpaid Amount": data?.lab?.unpaid_lab_amount,
      },
    },
    {
      id: "radiology",
      title: "Radiology",
      data: {
        "X-Ray Revenue": data?.radiology?.xray_revenue,
        "Unpaid X-Ray": data?.radiology?.unpaid_xray_amount,
        "Ultrasound Revenue": data?.radiology?.ultrasound_revenue,
        "Unpaid Ultrasound": data?.radiology?.unpaid_ultrasound_amount,
      },
    },
    {
      id: "employee_financials",
      title: "Employee Financials",
      customComponent: (
        <ProfessionalTable
          title="Employee Financials Summary"
          rows={[
            {
              label: "Total Payroll",
              value: employeeFinancialsData.total_payroll || 0,
            },
            {
              label: "Total Advances",
              value: employeeFinancialsData.total_advances || 0,
            },
            {
              label: "Total Bonuses",
              value: employeeFinancialsData.total_bonuses || 0,
            },
            {
              label: "Total Employee Financials",
              value: employeeFinancialsData.total_employee_financials || 0,
              isTotal: true,
            },
          ]}
          columns={["label", "value"]}
          onRowClick={handleRowClick}
        />
      ),
    },
    {
      id: "payroll_and_expenses",
      title: "Payroll & Expenses",
      data: {
        "Total Payroll": data?.payroll_and_expenses?.total_payroll,
        "Total Expenses": data?.payroll_and_expenses?.total_expenses,
      },
    },
    {
      id: "medicine_sales",
      title: "Medicine Sales",
      data: {
        "Total Sales": data?.medicine_sales?.total_medicine_sales,
        "Unpaid Sales": data?.medicine_sales?.unpaid_medicine_sales,
      },
    },
    {
      id: "prescription",
      title: "Prescription Sales",
      data: {
        "Total Sales": data?.prescription?.total_prescription_sales,
        "Unpaid Sales": data?.prescription?.unpaid_prescription_sales,
      },
    },
    {
      id: "echo",
      title: "Echo",
      data: {
        "Total Revenue": data?.echo?.total_echo_revenue,
        "Unpaid Amount": data?.echo?.unpaid_echo_amount,
      },
    },
    {
      id: "purchase",
      title: "Purchases",
      data: {
        "Total Expenses": data?.purchase?.total_purchase_expenses,
        "Unapproved Expenses": data?.purchase?.unapproved_purchase_expenses,
      },
    },
    {
      id: "dental",
      title: "Dental",
      data: {
        "Total Amount": data?.dental?.total_amount,
        "Collected Amount": data?.dental?.collected_amount,
        "Remaining Amount": data?.dental?.remaining_amount,
      },
    },
    {
      id: "summary",
      title: "Financial Summary",
      customComponent: data?.summary && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <SummaryCard
              title="Total Income"
              value={data.summary.total_income}
              change={data.summary.income_change || 0}
              color={{
                background: theme.palette.success.light,
                text: theme.palette.success.dark,
                border: theme.palette.success.main,
              }}
              onClick={() => handleSummaryClick("Total Income")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryCard
              title="Total Expenses"
              value={data.summary.total_expenses}
              change={data.summary.expense_change || 0}
              color={{
                background: theme.palette.error.light,
                text: theme.palette.error.dark,
                border: theme.palette.error.main,
              }}
              onClick={() => handleSummaryClick("Total Expenses")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryCard
              title="Net Income"
              value={data.summary.net_income}
              change={data.summary.net_change || 0}
              color={{
                background:
                  data.summary.net_income >= 0
                    ? theme.palette.success.light
                    : theme.palette.error.light,
                text:
                  data.summary.net_income >= 0
                    ? theme.palette.success.dark
                    : theme.palette.error.dark,
                border:
                  data.summary.net_income >= 0
                    ? theme.palette.success.main
                    : theme.palette.error.main,
              }}
              onClick={() => handleSummaryClick("Net Income")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryCard
              title="Collected Amount"
              value={calculateCollectedAmount()}
              color={{
                background: theme.palette.info.light,
                text: theme.palette.info.dark,
                border: theme.palette.info.main,
              }}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  const downloadPDF = async () => {
    if (!reportRef.current) return;

    const elementsToHide = document.querySelectorAll(".no-print");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    const canvas = await html2canvas(reportRef.current, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
      windowWidth: document.documentElement.offsetWidth,
      windowHeight: document.documentElement.offsetHeight,
    });

    elementsToHide.forEach((el) => (el.style.display = ""));

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("landscape");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 10, 10, pdfWidth, pdfHeight);
    pdf.save("financial-report.pdf");
  };

  const handlePrint = () => {
    const originalContents = document.body.innerHTML;
    const printContents = reportRef.current.innerHTML;

    const printStyles = `
      <style>
        @page { size: landscape; margin: 10mm; }
        body { font-family: Arial, sans-serif; }
        .no-print { display: none !important; }
        .MuiPaper-root { box-shadow: none !important; page-break-inside: avoid; }
        .MuiGrid-container { display: flex !important; flex-wrap: wrap !important; }
        table { width: 100% !important; }
      </style>
    `;

    document.body.innerHTML = printStyles + printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  return (
    <Box
      sx={{ p: 3, bgcolor: theme.palette.background.default, width: "100%" }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 2,
          }}
        >
          Financial Report Dashboard
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={handleTimeRangeChange}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="hour">Last Hour</MenuItem>
                <MenuItem value="day">Last 24 Hours</MenuItem>
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMMM d, yyyy h:mm aa"
              customInput={<DateTimeInput label="Start Date & Time" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMMM d, yyyy h:mm aa"
              customInput={<DateTimeInput label="End Date & Time" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleFilter}
              sx={{ height: "40px" }}
              className="no-print"
            >
              Generate Report
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box
              display="flex"
              justifyContent="flex-end"
              gap={1}
              className="no-print"
            >
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={downloadPDF}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Print
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="body1" fontWeight={600}>
            Error loading report:
          </Typography>
          {error?.message}
        </Alert>
      ) : (
        <Box ref={reportRef} sx={{ mt: 2 }}>
          {allSections.map((section) => {
            if (section.id === "summary") {
              return (
                <Box key={section.id} mb={4}>
                  {section.customComponent}
                </Box>
              );
            }

            return (
              <Paper
                key={section.id}
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection(section.id)}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ color: theme.palette.common.white }}
                  >
                    {expandedSections.includes(section.id) ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={expandedSections.includes(section.id)}>
                  <Box p={3}>
                    {section.customComponent ? (
                      section.customComponent
                    ) : section.isCurrencyBased ? (
                      <ProfessionalTable
                        title={section.title}
                        rows={section.tableData.rows}
                        columns={section.tableData.columns}
                        onRowClick={handleRowClick}
                      />
                    ) : (
                      <ProfessionalTable
                        title={section.title}
                        rows={Object.entries(section.data || {}).map(
                          ([label, value]) => ({
                            label,
                            value: value ?? "N/A",
                          })
                        )}
                        columns={["label", "value"]}
                        onRowClick={handleRowClick}
                      />
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Report;
